<template>
  <div class="badge-level-picker" ref="pickerContainer">
    <!-- Label -->
    <div class="picker-field-label">Readiness Level*</div>

    <!-- Closed State / Trigger -->
    <div
      ref="pickerTrigger"
      class="picker-trigger"
      :class="{ 'picker-trigger--open': isOpen, 'picker-trigger--locked': isLocked }"
      @click.stop="toggleDropdown"
      tabindex="0"
      @keydown.enter.stop="toggleDropdown"
      @keydown.space.prevent.stop="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <!-- Floating Label when open -->
      <div v-if="isOpen" class="picker-floating-label">Choose Readiness Level</div>
      
      <div class="picker-content">
        <div class="picker-selected-content">
          <div class="picker-checkbox" :class="getCheckboxClass(selectedLevel)"></div>
          <span class="picker-label">
            <span class="picker-label-name">{{ getSelectedLabelName() }}</span>
            <span class="picker-label-description">{{ getSelectedLabelDescription() }}</span>
          </span>
        </div>
      </div>
      <v-icon
        class="picker-chevron"
        :class="{ 'picker-chevron--rotated': isOpen }"
      >
        mdi-chevron-down
      </v-icon>
    </div>

    <!-- Dropdown menu (moved to document.body when open — Vue 2 has no Teleport) -->
    <div
      v-if="isOpen && !isLocked"
      ref="pickerDropdown"
      class="picker-dropdown picker-dropdown--portaled"
      :data-badge-picker-portal="portalId"
      :style="dropdownStyle"
    >
      <div class="picker-menu">
        <div
          v-for="option in readinessLevels"
          :key="option.value"
          class="picker-menu-item"
          :class="{
            'picker-menu-item--selected': selectedLevel === option.value,
          }"
          @click.stop="selectLevel(option.value, $event)"
          tabindex="0"
          @keydown.enter="selectLevel(option.value, $event)"
        >
          <div class="picker-menu-item-content">
            <div class="picker-menu-item-content-text">
              <div
                class="picker-checkbox"
                :class="getCheckboxClass(option.value)"
              >
              </div>
              <span class="picker-menu-label">{{ option.label }}</span>
              <span class="picker-menu-description">{{ option.description }}</span>
            </div>
            <svg
              v-if="selectedLevel === option.value"
              class="checkmark-selected"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M16.6673 5L7.50065 14.1667L3.33398 10"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from "vue";
import { useBodyPortalDropdown } from "@components/utils/useBodyPortalDropdown";
import { normalizeBadgeLevel, getBadgeLevelDisplayLabel } from "@components/utils/normalizeBadgeLevel";

const instance = getCurrentInstance();
const portalId = `badge-level-picker-portal-${instance?._uid ?? instance?.uid ?? Date.now()}`;

const ALL_LEVELS = [
  { value: "apprentice", label: "Apprentice", description: "can do with supervision" },
  { value: "proficient", label: "Proficient", description: "can do without supervision" },
  { value: "coach", label: "Coach", description: "can coach and inspect others" },
  { value: "architect", label: "Architect", description: "creates standards" },
];

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  levels: {
    type: Array,
    default: null,
  },
  showArchitect: {
    type: Boolean,
    default: true,
  },
  lockAfterSelection: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "change"]);

const pickerContainer = ref(null);
const pickerTrigger = ref(null);
const pickerDropdown = ref(null);
const isOpen = ref(false);
const lockedAfterSelection = ref(false);

// Legacy option values/labels resolve to the new keys/labels.
const readinessLevels = computed(() =>
  (props.levels ?? ALL_LEVELS)
    .map((option) => ({
      ...option,
      value: normalizeBadgeLevel(option.value) ?? option.value,
      label: getBadgeLevelDisplayLabel(option.value) ?? option.label,
    }))
    .filter((option) => props.showArchitect || option.value !== "architect")
);
const resolveLevelValue = (value) => normalizeBadgeLevel(value) ?? value;
const selectedLevel = ref(
  resolveLevelValue(props.modelValue) ?? readinessLevels.value[0]?.value ?? "apprentice"
);
const isLocked = computed(
  () =>
    (readinessLevels.value?.length === 1) ||
    (props.lockAfterSelection && lockedAfterSelection.value)
);

const getSelectedLabelName = () => {
  const option = readinessLevels.value.find((opt) => opt.value === selectedLevel.value);
  return option ? option.label : "";
};

const getSelectedLabelDescription = () => {
  const option = readinessLevels.value.find((opt) => opt.value === selectedLevel.value);
  return option ? option.description : "";
};


const getCheckboxClass = (level) => {
  const classes = {
    apprentice: "checkbox-apprentice",
    proficient: "checkbox-proficient",
    coach: "checkbox-coach",
    architect: "checkbox-architect",
  };
  return classes[normalizeBadgeLevel(level)] || "";
};

// When locked, keep dropdown closed
watch(
  isLocked,
  (val) => {
    if (val) isOpen.value = false;
  }
);

// Sync with parent's modelValue prop changes
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== null && newVal !== undefined) {
      selectedLevel.value = resolveLevelValue(newVal);
    }
  }
);

// When the available levels change, ensure selectedLevel is still valid
watch(
  () => props.levels,
  (newLevels) => {
    if (!newLevels) return;
    const isCurrentValid = readinessLevels.value.some((l) => l.value === selectedLevel.value);
    if (!isCurrentValid && newLevels.length > 0) {
      selectedLevel.value = readinessLevels.value[0].value;
      emit("update:modelValue", selectedLevel.value);
      emit("change", selectedLevel.value);
    }
  },
  { immediate: true }
);

const closeDropdown = () => {
  isOpen.value = false;
};

const { dropdownStyle, attach, detach } = useBodyPortalDropdown({
  getAnchor: () => pickerTrigger.value,
  getDropdown: () => pickerDropdown.value,
  getContainer: () => pickerContainer.value,
  onClose: closeDropdown,
});

const toggleDropdown = (event) => {
  if (event) {
    event.stopPropagation();
  }
  if (isLocked.value) return;
  isOpen.value = !isOpen.value;
};

const selectLevel = (levelValue, event) => {
  if (event) {
    event.stopPropagation();
  }
  selectedLevel.value = levelValue;
  emit("update:modelValue", levelValue);
  emit("change", levelValue);
  closeDropdown();
  if (props.lockAfterSelection) lockedAfterSelection.value = true;
};

watch(isOpen, (open) => {
  if (open) {
    attach();
  } else {
    detach();
  }
});
</script>

<style scoped>
.badge-level-picker {
  position: relative;
  width: 100%;
  max-width: 440px;
  font-family: Inter, sans-serif;
}

.picker-field-label {
  color: var(--Headings-or-Titles, #344054);
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 9px;
}

.picker-floating-label {
  position: absolute;
  top: -8px;
  left: 12px;
  background-color: #fff;
  padding: 0 4px;
color: var(--Grey, #666);
font-family: Inter;
font-size: 8px;
font-weight: 400;
line-height: 12px;
}

.picker-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #9e77ed3d;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  min-height: 44px;
  user-select: none;
}

.picker-trigger:focus,
.picker-trigger--open {
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
    0px 0px 0px 4px rgba(158, 119, 237, 0.24);
}

/* Locked state: grayed bg, not clickable; label name black bold, chevron default */
.picker-trigger--locked {
  pointer-events: none;
  cursor: default;
  background-color: #E0E0E0 !important;
  border-color: #e4e7ec !important;
  box-shadow: none !important;
}

.picker-trigger--locked .picker-label,
.picker-trigger--locked .picker-label-description {
  color: #98a2b3 !important;
}
.picker-trigger--locked .picker-chevron {
  color: #666666 !important;
}

.picker-trigger--locked .picker-label-name {
  color: #000000 !important;
  font-weight: 600 !important;
}

.picker-locked-message {
  margin-top: 6px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

/* Trigger checkbox keeps its level-specific color when open */

.picker-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-top: 0;
  transition: margin-top 0.2s ease;
}

.picker-selected-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.picker-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  gap: 4px;
}

.picker-label-name {
  font-weight: 400;
}

.picker-label-description {
  font-weight: 400;
  color: #98A2B3;
}

.picker-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

/* Level-specific border colors for unselected checkboxes */
.checkbox-apprentice {
  border-color: #C17200 !important;
  background-color: #FFF5D3 !important;
}

.checkbox-proficient {
  border-color: #00B813     !important;
  background-color: #F3FFF9 !important;
}

.checkbox-coach {
  border-color: #00A3FF !important;
  background-color: #EFFEFF !important;
}

.checkbox-architect {
  border-color: #2E1EC1 !important;
  background-color: #F2F3FF !important;
}

/* Selected item checkmark is always purple */
.checkmark-selected path {
  stroke: #9e77ed !important;
}

.picker-checkbox svg {
  display: block;
}

.picker-chevron {
  flex-shrink: 0;
  color: #667085 !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.picker-chevron--rotated {
  transform: rotate(180deg);
}

.picker-dropdown {
  font-family: Inter, sans-serif;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

/* Portaled to body by useBodyPortalDropdown; position, width and z-index come from its bound style */
.picker-dropdown--portaled {
  max-height: min(320px, calc(100vh - 24px));
  overflow-y: auto;
}

.picker-menu {
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

}

.picker-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
}

.picker-menu-item:hover {
  background-color: #f9fafb;
}

.picker-menu-item:focus {
  background-color: #f9fafb;
}

/* Selected item: purple inner border, no tint background */
.picker-menu-item--selected {
  background-color: #F9FAFB !important;
}

.picker-menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  justify-content: space-between;
}

.picker-menu-label {
  color: #101828;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.picker-menu-description {
  color: #98A2B3;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  text-transform: lowercase;
}
.picker-menu-item-content-text {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-family: Inter;
}
</style>
